// @mui material components
import {
  Snackbar,
  Alert,
  Grid,
  Card,
  CircularProgress,
  Backdrop,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  DialogActions,
  Icon,
  Tooltip,
  Divider,
  MenuItem,
  InputLabel,
  Select,
  FormControl,
} from "@mui/material";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";

// Material Dashboard 2 React example components
import DashboardLayout from "app/LayoutContainers/DashboardLayout";
import DashboardNavbar from "app/Navbars/DashboardNavbar";
import Footer from "theme/Footer";

import { useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios.js";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";
import MDBadge from "theme/components/MDBadge";
import DataTable from "theme/Tables/DataTable";

// Images
import masterCardLogo from "assets/images/logos/mastercard.png";
import visaLogo from "assets/images/logos/visa.png";
import usePageTitle from "app/pageTitle";

function VCard() {
  const pageTitle = "Virtual Card";
  usePageTitle(pageTitle);

  const { reference } = useParams();
  const [refreshPage, setRefreshPage] = useState(false);
  const navigate = useNavigate();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [card, setVcard] = useState(null);
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [withdrawalFee, setWithdrawalFee] = useState(0.0);
  const [fundingFeeObject, setFundingFeeObject] = useState(null);

  const [fundingFee, setFundingFee] = useState(0.0);
  const [fundingBalances, setFundingBalances] = useState([]);
  const [fundingBalance, setFundingBalance] = useState(null);

  const [withdrawalBalances, setWithdrawalBalances] = useState([]);
  const [withdrawalBalance, setWithdrawalBalance] = useState(null);

  const [currentAmount, setCurrentAmount] = useState(0);
  const [openFundDialog, setOpenFundDialog] = useState(false);
  const [openWithdrawDialog, setOpenWithdrawDialog] = useState(false);
  const handleOpenFundDialog = () => {
    setOpenFundDialog(true);
  };

  function formatAmount(amount, currency) {
    const prefix = amount < 0 ? "-" : "";
    amount = Math.abs(amount);
    return `${prefix}${currency}${amount.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  const tableColumns = [
    { Header: "Type", accessor: "type", align: "left" },
    { Header: "Amount", accessor: "amount", align: "center" },
    { Header: "Narration", accessor: "narration", align: "center" },
    { Header: "Reference", accessor: "reference", align: "center" },
    { Header: "Created", accessor: "created", align: "right" },
  ];

  const buildTableRows = (data) => {
    const rows = [];
    data.map((transaction) => {
      let statusColor = "warning";
      if (transaction.type === "CREDIT") {
        statusColor = "success";
      } else {
        statusColor = "error";
      }

      rows.push({
        reference: transaction.reference,
        narration: transaction.narration,
        amount: formatAmount(transaction.amount / 100, transaction.currency.symbol),
        type: (
          <MDBadge
            badgeContent={transaction.type}
            variant="contained"
            container
            color={statusColor}
          />
        ),
        created: transaction.created,
      });
      return null;
    });

    return rows;
  };

  useEffect(() => {
    const fetchData = async () => {
      setShowLoading(true);
      await axios
        .get(`users/vcard/${reference}/get.json`)
        .then((result) => {
          // console.log(result);
          setShowLoading(false);
          if (result.data.status === "success") {
            setVcard(result.data.data.card);
            setBalance(result.data.data.balance);
            setWithdrawalFee(result.data.data.withdrawalFee);
            setFundingFeeObject(result.data.data.fundingFee);
            setFundingBalances(result.data.data.funding_balances);
            setWithdrawalBalances(result.data.data.withdrawal_balances);

            const rows = buildTableRows(result.data.data.transactions);
            setTransactions(rows);
          } else {
            setSeverity("error");
            setErrMsg(result.data.data.message);
            setOpen(true);
          }
        })
        .catch(() => {
          setShowLoading(false);
          setSeverity("error");
          setErrMsg("An error occurred. Please try again in a few moments.");
          setOpen(true);
        });
    };

    fetchData();
  }, [refreshPage]);

  async function fundCard() {
    setShowLoading(true);
    const json = { reference: `${reference}`, amount: currentAmount * 100, source: fundingBalance };
    await axios
      .post("users/vcard/fund.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  async function cardWithdraw() {
    setShowLoading(true);
    const json = {
      reference: `${reference}`,
      amount: currentAmount * 100,
      destination: withdrawalBalance,
    };
    await axios
      .post("users/vcard/withdraw.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  const handleFundDialogClose = () => {
    setFundingFee("0.00");
    setOpenFundDialog(false);
  };

  const handleOpenWithdrawDialog = () => {
    setOpenWithdrawDialog(true);
  };

  const handleWithdrawDialogClose = () => {
    setOpenWithdrawDialog(false);
  };

  function handleFundCardSubmit(event) {
    event.preventDefault();
    if (currentAmount <= 0) {
      setSeverity("error");
      setErrMsg("Enter a valid amount.");
      setOpen(true);
      return;
    }

    if (fundingBalance === null) {
      setSeverity("error");
      setErrMsg("Select a funding balance.");
      setOpen(true);
      return;
    }

    handleFundDialogClose();
    fundCard();
  }

  function handleWithdrawCardSubmit(event) {
    event.preventDefault();
    if (currentAmount <= 0) {
      setSeverity("error");
      setErrMsg("Enter a valid amount.");
      setOpen(true);
      return;
    }

    if (withdrawalBalance === null) {
      setSeverity("error");
      setErrMsg("Select a destination balance.");
      setOpen(true);
      return;
    }

    handleWithdrawDialogClose();
    cardWithdraw();
  }

  const [openCancelDialog, setCancelDialog] = useState(false);
  const [openStateDialog, setOpenStateDialog] = useState(false);

  async function changeCardState() {
    setShowLoading(true);
    const json = { reference: `${reference}`, freeze: !card.frozen };
    await axios
      .post("users/vcard/freeze.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          setRefreshPage(!refreshPage);
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  async function terminateCard() {
    setShowLoading(true);
    const json = { reference: `${reference}` };
    await axios
      .post("users/vcard/terminate.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg("Card terminated successfully.");
          setOpen(true);

          navigate("/cards");
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg("An error occurred. Make sure you have an active internet connection.");
        setOpen(true);
      });
  }

  function calculateFundingFee(uAmt) {
    setCurrentAmount(uAmt);

    const amount = uAmt * 100;
    const feeObject = fundingFeeObject[card.currency.currency_code];
    let tFee = feeObject.fee;
    if (feeObject.is_percentage) {
      tFee = amount * feeObject.fee;
      if (feeObject.minimum > 0) {
        tFee = feeObject.minimum > tFee ? feeObject.minimum : tFee;
      }

      if (feeObject.maximum > 0) {
        tFee = feeObject.maximum < tFee ? feeObject.maximum : tFee;
      }

      tFee += feeObject.mandatory_fee;
    }

    let cDiscount = feeObject.discount;
    if (feeObject.is_discount_percentage) {
      cDiscount = tFee * feeObject.discount;
    }

    tFee -= cDiscount;
    setFundingFee(tFee / 100);
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {card != null ? (
        <>
          <Dialog open={openFundDialog} onClose={handleFundDialogClose}>
            <form onSubmit={handleFundCardSubmit}>
              <DialogTitle>
                <MDBox>Fund Card</MDBox>
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  <MDBox>Enter an amount to top up your card</MDBox>
                </DialogContentText>
                <MDBox sx={{ marginTop: "15px" }}>
                  <MDInput
                    name="amount"
                    margin="dense"
                    label={`${card.currency.currency_code} Amount`}
                    type="decimal"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => calculateFundingFee(e.target.value)}
                  />
                </MDBox>
                <MDBox mt={3}>
                  <FormControl sx={{ minWidth: 120, width: "100%" }}>
                    <InputLabel>Funding Balance</InputLabel>
                    <Select
                      label="Funding Balance"
                      onChange={(e) => {
                        setFundingBalance(e.target.value);
                      }}
                      sx={{ padding: "12px", paddingLeft: "3px" }}
                    >
                      {
                        // --> Notice below changes
                        fundingBalances.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                          fundingBalances.map((record) => (
                            <MenuItem value={record}>{record} Balance</MenuItem>
                          ))
                      }
                    </Select>
                  </FormControl>
                </MDBox>
                <MDBox mt={1}>
                  <MDTypography
                    pr={1}
                    variant="caption"
                    color="dark"
                    fontWeight="medium"
                    sx={{ display: "flex", justifyContent: "flex-end" }}
                  >
                    Fee: {formatAmount(fundingFee, card.currency.symbol)}
                  </MDTypography>
                </MDBox>
              </DialogContent>
              <DialogActions>
                <MDButton onClick={handleFundDialogClose} color="dark" variant="outlined">
                  Cancel
                </MDButton>
                <MDButton color="success" variant="outlined" type="submit">
                  Fund
                </MDButton>
              </DialogActions>
            </form>
          </Dialog>

          <Dialog open={openWithdrawDialog} onClose={handleWithdrawDialogClose}>
            <form onSubmit={handleWithdrawCardSubmit}>
              <DialogTitle>
                <MDBox>Withdraw Funds</MDBox>
              </DialogTitle>
              <DialogContent>
                <DialogContentText>
                  <MDBox>Enter an amount to withdraw to your balance</MDBox>
                </DialogContentText>
                <MDBox sx={{ marginTop: "15px" }}>
                  <MDInput
                    name="amount"
                    margin="dense"
                    label={`${card.currency.currency_code} Amount`}
                    type="decimal"
                    fullWidth
                    variant="outlined"
                    onChange={(e) => setCurrentAmount(e.target.value)}
                  />
                </MDBox>
                <MDBox mt={2}>
                  <FormControl sx={{ minWidth: 120, width: "100%" }}>
                    <InputLabel>Destination Balance</InputLabel>
                    <Select
                      label="Destination Balance"
                      onChange={(e) => {
                        setWithdrawalBalance(e.target.value);
                      }}
                      sx={{ padding: "12px", paddingLeft: "3px" }}
                    >
                      {
                        // --> Notice below changes
                        withdrawalBalances.length > 0 && // --> Changes from `options.length ? ...` to `options.length > 0 && ...`
                          withdrawalBalances.map((record) => (
                            <MenuItem value={record}>{record} Balance</MenuItem>
                          ))
                      }
                    </Select>
                  </FormControl>
                </MDBox>
                <MDBox mt={2}>
                  <Alert severity="warning">
                    <small>
                      {formatAmount(withdrawalFee / 100, card.currency.symbol)} withdrawal fee will
                      be applied.
                    </small>
                  </Alert>
                </MDBox>
              </DialogContent>
              <DialogActions>
                <MDButton onClick={handleWithdrawDialogClose} color="dark" variant="outlined">
                  Cancel
                </MDButton>
                <MDButton color="error" variant="outlined" type="submit">
                  Withdraw
                </MDButton>
              </DialogActions>
            </form>
          </Dialog>
          <Dialog open={openStateDialog}>
            <DialogTitle>
              <MDBox>{card.frozen ? "Unfreeze" : "Freeze"} Card?</MDBox>
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                <MDBox>
                  Are you sure you want to {card.frozen ? "unfreeze" : "freeze"} this card?
                </MDBox>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <MDButton
                onClick={() => {
                  setOpenStateDialog(false);
                }}
                color="dark"
                variant="outlined"
              >
                Cancel
              </MDButton>
              <MDButton
                color="info"
                variant="outlined"
                onClick={() => {
                  setOpenStateDialog(false);
                  changeCardState();
                }}
              >
                Proceed
              </MDButton>
            </DialogActions>
          </Dialog>
          <Dialog open={openCancelDialog}>
            <DialogTitle>
              <MDBox>Delete Card?</MDBox>
            </DialogTitle>
            <DialogContent>
              <DialogContentText>
                <MDBox>
                  Are you sure you want to delete this card? Any funds left will be lost if you
                  proceed.
                </MDBox>
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <MDButton
                onClick={() => {
                  setCancelDialog(false);
                }}
                color="dark"
                variant="outlined"
              >
                Cancel
              </MDButton>
              <MDButton
                color="warning"
                variant="outlined"
                onClick={() => {
                  setCancelDialog(false);
                  terminateCard();
                }}
              >
                Delete
              </MDButton>
            </DialogActions>
          </Dialog>
        </>
      ) : (
        <> </>
      )}
      <MDBox pt={3} pb={3}>
        {card != null ? (
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Grid item xs={12} md={12} lg={12}>
                <MDBox sx={{ marginBottom: "35px" }}>
                  <Card>
                    <MDBox p={2} pl={3}>
                      <MDBox
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: "100%", marginRight: "10px" }}
                      >
                        <MDBox>
                          <MDTypography variant="h5" fontWeight="medium">
                            {card.title}
                          </MDTypography>
                        </MDBox>
                        <MDBox lineHeight={1}>
                          <Tooltip title="Delete Card" placement="top">
                            <MDButton
                              variant="outlined"
                              color="warning"
                              iconOnly
                              circular
                              onClick={() => {
                                setCancelDialog(true);
                              }}
                            >
                              <Icon sx={{ cursor: "pointer" }} fontSize="small" color="dark">
                                delete
                              </Icon>
                            </MDButton>
                          </Tooltip>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  </Card>
                </MDBox>
              </Grid>
              <Grid container spacing={3}>
                <Grid item xs={12} md={7} lg={7}>
                  <Card>
                    <MDBox
                      borderRadius="lg"
                      p={3}
                      sx={{
                        marginLeft: "15px",
                        marginTop: "15px",
                        marginRight: "15px",
                        marginBottom: "15px",
                        border: ({ borders: { borderWidth, borderColor } }) =>
                          `${borderWidth[1]} solid ${borderColor}`,
                      }}
                    >
                      <Grid container spacing={3}>
                        <Grid item xs={12} lg={12} md={12}>
                          <MDBox display="flex" justifyContent="space-between" alignItems="center">
                            <MDBox
                              component="img"
                              src={card.details.brand === "mastercard" ? masterCardLogo : visaLogo}
                              alt={card.brand}
                              width="10%"
                              mr={2}
                            />
                            <MDBox
                              lineHeight={0}
                              sx={{ marginBottom: "15px", textAlign: "center" }}
                            >
                              <MDTypography variant="caption" color="text">
                                Currency:&nbsp;
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                fontWeight="medium"
                                textTransform="capitalize"
                              >
                                {card.currency.currency_code}
                              </MDTypography>
                            </MDBox>
                          </MDBox>
                          <MDBox lineHeight={1} sx={{ marginTop: "15px", textAlign: "center" }}>
                            <MDTypography variant="h4" fontWeight="medium">
                              {card.details.pan.substring(0, 4)}
                              &nbsp;{"   "}&nbsp;
                              {card.details.pan.substring(4, 8)}
                              &nbsp;{"   "}&nbsp;
                              {card.details.pan.substring(8, 12)}
                              &nbsp;{"   "}&nbsp;
                              {card.details.pan.substring(12, 16)}
                            </MDTypography>
                          </MDBox>
                        </Grid>
                        <Grid item xs={12} lg={12} md={12}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} lg={12} md={12}>
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <MDBox mr={3} lineHeight={1}>
                                  <MDTypography variant="button" fontWeight="regular" opacity={0.8}>
                                    Card Holder
                                  </MDTypography>
                                  <MDTypography
                                    variant="h6"
                                    fontWeight="medium"
                                    textTransform="capitalize"
                                  >
                                    {card.details.cardholder}
                                  </MDTypography>
                                </MDBox>
                                <MDBox mr={3} lineHeight={1}>
                                  <MDTypography variant="button" fontWeight="regular" opacity={0.8}>
                                    Expires
                                  </MDTypography>
                                  <MDTypography variant="h6" fontWeight="medium">
                                    {card.details.exp_date}
                                  </MDTypography>
                                </MDBox>
                                <MDBox lineHeight={1}>
                                  <MDTypography variant="button" fontWeight="regular" opacity={0.8}>
                                    CVV
                                  </MDTypography>
                                  <MDTypography variant="h6" fontWeight="medium">
                                    {card.details.cvv}
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12} lg={12} md={12}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} lg={12} md={12}>
                              <MDBox
                                display="flex"
                                alignItems="center"
                                justifyContent="space-between"
                              >
                                <MDBox mr={3} lineHeight={1}>
                                  <MDTypography variant="button" fontWeight="regular" opacity={0.8}>
                                    Billing Address
                                  </MDTypography>
                                  <MDTypography variant="h6" fontWeight="medium">
                                    {card.details.billing_address}
                                  </MDTypography>
                                </MDBox>
                                <MDBox lineHeight={1}>
                                  <MDTypography
                                    variant="button"
                                    fontWeight="regular"
                                    opacity={0.8}
                                    align="right"
                                  >
                                    Postal Code
                                  </MDTypography>
                                  <MDTypography variant="h6" fontWeight="medium" align="right">
                                    {card.details.billing_postal_code}
                                  </MDTypography>
                                </MDBox>
                              </MDBox>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                </Grid>
                <Grid item xs={12} md={5} lg={5}>
                  <MDBox sx={{ marginBottom: "35px" }}>
                    <Card>
                      <MDBox p={3}>
                        <MDBox
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          sx={{ width: "100%", marginRight: "10px" }}
                        >
                          <MDBox>
                            <MDTypography variant="h6" fontWeight="regular" opacity={0.8}>
                              Balance:
                            </MDTypography>
                          </MDBox>
                          <MDBox>
                            <MDTypography variant="h6">
                              {formatAmount(balance / 100, card.currency.symbol)}
                            </MDTypography>
                          </MDBox>
                        </MDBox>
                        <MDBox m={3}>
                          <Divider color="dark" />
                        </MDBox>
                        <MDBox marginTop="35px" display="flex" justifyContent="right">
                          <MDBox
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{ width: "100%" }}
                          >
                            <MDBox lineHeight={1}>
                              <Tooltip
                                title={card.frozen ? "UnFreeze Card" : "Freeze Card"}
                                placement="top"
                              >
                                <MDButton
                                  variant="outlined"
                                  color="info"
                                  iconOnly
                                  circular
                                  onClick={() => {
                                    setOpenStateDialog(true);
                                  }}
                                >
                                  <Icon sx={{ cursor: "pointer" }} fontSize="small">
                                    {card.frozen ? "attach_money" : "money_off"}
                                  </Icon>
                                </MDButton>
                              </Tooltip>
                            </MDBox>
                            <MDBox lineHeight={1}>
                              <Tooltip title="Withdraw Funds" placement="top">
                                <MDButton
                                  variant="outlined"
                                  color="error"
                                  iconOnly
                                  circular
                                  onClick={() => {
                                    handleOpenWithdrawDialog(card.reference);
                                  }}
                                >
                                  <Icon sx={{ cursor: "pointer" }} fontSize="small">
                                    remove
                                  </Icon>
                                </MDButton>
                              </Tooltip>
                              &nbsp;&nbsp;&nbsp;&nbsp;
                              <Tooltip title="Add Funds" placement="top">
                                <MDButton
                                  variant="outlined"
                                  color="success"
                                  iconOnly
                                  circular
                                  onClick={() => {
                                    handleOpenFundDialog(card.reference);
                                  }}
                                >
                                  <Icon sx={{ cursor: "pointer" }} fontSize="small">
                                    add
                                  </Icon>
                                </MDButton>
                              </Tooltip>
                            </MDBox>
                          </MDBox>
                        </MDBox>
                      </MDBox>
                    </Card>
                  </MDBox>
                </Grid>
              </Grid>
              <MDBox pt={3} pb={3}>
                <Grid container spacing={6}>
                  <Grid item xs={12}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={12} lg={12}>
                        <MDBox>
                          <Card sx={{ height: "100%", marginTop: "0px" }}>
                            <MDBox p={3}>
                              <MDBox
                                pt={2}
                                px={2}
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <MDTypography variant="h6" fontWeight="medium">
                                  Card Transactions
                                </MDTypography>
                              </MDBox>
                              <MDBox mt={5}>
                                <DataTable
                                  table={{ columns: tableColumns, rows: transactions }}
                                  isSorted={false}
                                  entriesPerPage={false}
                                  showTotalEntries={false}
                                  noEndBorder
                                />
                              </MDBox>
                            </MDBox>
                          </Card>
                        </MDBox>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </MDBox>
            </Grid>
          </Grid>
        ) : (
          <> </>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default VCard;
